import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet,Image } from '@react-pdf/renderer';
import img from '../images-src/logo.jpeg';

const QuotationList = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewQuotation, setPreviewQuotation] = useState(null);
  const [searchQuotationNo, setSearchQuotationNo] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateQuotationTotal = (quotation) => {
    const lines = quotation.qlines || quotation.items || [];
    if (quotation.total) return parseFloat(quotation.total);
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const subtotal = quantity * unitPrice;
      const afterDiscount = subtotal - discount;
      const vat = afterDiscount * (vatPercent / 100);
      const total = afterDiscount + vat;
      return sum + total;
    }, 0);
  };

  const calculateQuotationVAT = (quotation) => {
    const lines = quotation.qlines || quotation.items || [];
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      const vat = afterDiscount * (vatPercent / 100);
      return sum + vat;
    }, 0);
  };

  useEffect(() => {
    const fetchQuotations = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/quotations`);
        const payload = response.data;
        const quotationList = Array.isArray(payload)
          ? payload
          : payload.data || [];
        setQuotations(quotationList);
      } catch (err) {
        console.error('Error fetching quotations:', err);
        setError('Unable to load quotations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, [apiUrl]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  // Filter quotations based on search
  const filteredQuotations = quotations.filter(quotation => {
    const quotationNo = (quotation.quotation_no || quotation.quotationNo || '').toLowerCase();
    const customerName = (quotation.customer?.name || quotation.customerName || '').toLowerCase();

    const matchesQuotationNo = searchQuotationNo === '' || quotationNo.includes(searchQuotationNo.toLowerCase());
    const matchesCustomer = searchCustomer === '' || customerName.includes(searchCustomer.toLowerCase());

    return matchesQuotationNo && matchesCustomer;
  });

  const handleDelete = async (quotationId) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        await axios.delete(`${apiUrl}/api/quotations/${quotationId}`);
        setQuotations(quotations.filter(quotation => quotation.id !== quotationId));
      } catch (err) {
        console.error('Error deleting quotation:', err);
        setError('Failed to delete quotation. Please try again.');
      }
    }
  };

  const quotationPdfStyles = StyleSheet.create({
    page: {
      padding: 10,
      fontSize: 8,
      fontFamily: 'Helvetica',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      borderBottomWidth: 2,
      borderBottomColor: '#0891b2',
      paddingBottom: 20,
    },
    companyName: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#0891b2',
    },
    companyDetails: {
      fontSize: 9,
      color: '#333',
      marginTop: 2,
    },
    invoiceTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
     subtitle0: {
      fontSize: 12,
      color: '#666',
      textAlign: 'right',
      marginTop: 5,
      fontWeight: 'bold',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      textAlign: 'right',
      marginTop: 5,
    },
    infoSection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 15,
      backgroundColor: '#ffffff',
      padding: 10,
    },
    infoBlock: {
      flex: 1,
      marginRight: 15,
    },
    infoLabel: {
      fontSize: 9,
      color: '#333',
      marginBottom: 2,
      textTransform: 'uppercase',
      fontWeight: 'bold',
    },
    infoValue: {
      fontSize: 11,
      fontWeight: '600',
      color: '#000',
    },
    customerSection: {
      marginBottom: 20,
      padding: 10,
      backgroundColor: '#ffffff',
    },
    table: {
      marginBottom: 20,
      borderWidth: 1,
      borderColor: '#ddd',
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#0891b2',
      color: 'white',
      padding: 10,
      fontWeight: 'bold',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#ddd',
      padding: 8,
    },
    tableRowAlt: {
      backgroundColor: '#f9f9f9',
    },
    col1: { width: '30%', paddingHorizontal: 5 },
    col2: { width: '8%', textAlign: 'center', paddingHorizontal: 5 },
    col3: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
    col4: { width: '12%', textAlign: 'right', paddingHorizontal: 5 },
    col5: { width: '12%', textAlign: 'right', paddingHorizontal: 5 },
    col6: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
    col7: { width: '12%', textAlign: 'right', paddingHorizontal: 5 },
    discountTotalRow: {
      flexDirection: 'row',
      borderBottomWidth: 2,
      borderBottomColor: '#0891b2',
      backgroundColor: '#f3f4f6',
      padding: 8,
      fontWeight: 'bold',
    },
    totals: {
      marginTop: 20,
      alignItems: 'flex-end',
    },
    totalRow: {
      flexDirection: 'row',
      padding: 5,
      width: '100%',
    },
    totalRowHalf: {
      flexDirection: 'row',
      padding: 5,
      width: '48%',
      marginRight: '2%',
      marginBottom: 5,
    },
    totalLabel: {
      flex: 1,
      textAlign: 'right',
      marginRight: 10,
      fontSize: 11,
    },
    totalValue: {
      width: 100,
      textAlign: 'right',
      fontSize: 11,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
    },
    summaryBox: {
      width: '48%',
      padding: 10,
      backgroundColor: '#f0f9fc',
      borderWidth: 1,
      borderColor: '#0891b2',
    },
    summaryLabel: {
      fontSize: 10,
      color: '#666',
      marginBottom: 5,
      fontWeight: 'bold',
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
    grandTotal: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      borderTopWidth: 2,
      borderTopColor: '#0891b2',
      paddingTop: 10,
      marginTop: 10,
      width: '100%',
    },
    notes: {
      marginTop: 30,
      padding: 15,
      backgroundColor: '#f8f9fa',
    },
    notesTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      marginBottom: 8,
      color: '#0891b2',
    },
    notesText: {
      fontSize: 10,
      lineHeight: 1.4,
      color: '#666',
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      textAlign: 'center',
      color: '#666',
      fontSize: 9,
      borderTopWidth: 1,
      borderTopColor: '#ddd',
      paddingTop: 10,
    },
    compliance: {
      fontSize: 8,
      color: '#0891b2',
      marginTop: 5,
    },
  });

  const formatCurrencyPdf = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const QuotationPDFDocument = ({ quotation }) => {
    const lines = (quotation && (quotation.qlines || quotation.items)) || [];
    const customerName = (quotation && (quotation.customer?.name || quotation.customerName)) || 'Unknown Customer';
    const quotationDate = quotation && (quotation.quotation_date || quotation.date);
    
    const total = calculateQuotationTotal(quotation || {});
    const subtotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      return sum + (quantity * unitPrice);
    }, 0);
    const totalDiscount = lines.reduce((sum, line) => {
      return sum + parseFloat(line?.discount_amount || line?.discountAmount || 0);
    }, 0);
    const taxableAmount = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
      return sum + (quantity * unitPrice - discount);
    }, 0);
    const vatTotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
      const vatPercent = parseFloat(line?.vat_percent || line?.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      return sum + (afterDiscount * (vatPercent / 100));
    }, 0);

    return (
      <Document>
        <Page size="A4" style={quotationPdfStyles.page}>
          <View style={quotationPdfStyles.header}>
            <View >
                <Image src={img} style={{ width: 90, height: 110, marginBottom: 5,marginLeft: 100  }} />
            </View>
            <View>
              <Text style={quotationPdfStyles.invoiceTitle}>QUOTATION</Text>
              <Text style={quotationPdfStyles.subtitle0}>Diamond Quality Construction L.L.C.SP</Text>
            
          <Text style={quotationPdfStyles.subtitle}>TRN: 104836477000001</Text>

  <Text  style={quotationPdfStyles.subtitle}>Sharjah - Muweilah - University City Street</Text> 
  <Text  style={quotationPdfStyles.subtitle}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
  <Text  style={quotationPdfStyles.subtitle}>0557001522</Text>
  <Text  style={quotationPdfStyles.subtitle}>dqcc01@gmail.com</Text>
  <Text  style={quotationPdfStyles.subtitle}>www.dqcc.ae</Text>
            
            </View>
          </View>

          <View style={quotationPdfStyles.infoSection}>
            <View style={quotationPdfStyles.infoBlock}>
              <Text style={quotationPdfStyles.infoLabel}>Quotation Number</Text>
              <Text style={quotationPdfStyles.infoValue}>{quotation?.quotation_no || quotation?.quotationNo || 'N/A'}</Text>
            </View>
            <View style={quotationPdfStyles.infoBlock}>
              <Text style={quotationPdfStyles.infoLabel}>Quotation Date</Text>
              <Text style={quotationPdfStyles.infoValue}>{formatDate(quotationDate)}</Text>
            </View>
            <View style={quotationPdfStyles.infoBlock}>
              <Text style={quotationPdfStyles.infoLabel}>Quoted Amount</Text>
              <Text style={quotationPdfStyles.infoValue}>{formatCurrencyPdf(total)}</Text>
            </View>
          </View>

          <View style={quotationPdfStyles.customerSection}>
            <Text style={[quotationPdfStyles.infoLabel, { marginBottom: 8 }]}>QUOTED FOR</Text>
            <Text style={[quotationPdfStyles.infoValue, { marginTop: 3, marginBottom: 5 }]}>{customerName}</Text>
            {(quotation?.customer?.address || quotation?.customerAddress) && (
              <Text style={[quotationPdfStyles.companyDetails, { marginBottom: 3 }]}>Address: {quotation?.customer?.address || quotation?.customerAddress}</Text>
            )}
            {(quotation?.customer?.trn || quotation?.customerTrn) && (
              <Text style={[quotationPdfStyles.companyDetails, { marginBottom: 3 }]}>TRN No.: {quotation?.customer?.trn || quotation?.customerTrn}</Text>
            )}
            {(quotation?.customer?.phone || quotation?.customerPhone) && (
              <Text style={quotationPdfStyles.companyDetails}>Phone: {quotation?.customer?.phone || quotation?.customerPhone}</Text>
            )}
          </View>

          <View style={quotationPdfStyles.table}>
            <View style={quotationPdfStyles.tableHeader}>
              <Text style={quotationPdfStyles.col1}>ITEM DESCRIPTION</Text>
              <Text style={quotationPdfStyles.col2}>QTY.</Text>
              <Text style={quotationPdfStyles.col3}>UNIT</Text>
              <Text style={quotationPdfStyles.col4}>PRICE</Text>
              <Text style={quotationPdfStyles.col5}>DISCOUNT</Text>
              <Text style={quotationPdfStyles.col6}>VAT %</Text>
              <Text style={quotationPdfStyles.col7}>TOTAL</Text>
            </View>
            {lines.map((line, index) => {
              const quantity = parseFloat(line?.quantity || 0);
              const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
              const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
              const vatPercent = parseFloat(line?.vat_percent || line?.vatPercent || 0);
              const subtotalLine = quantity * unitPrice;
              const afterDiscount = subtotalLine - discount;
              const vat = afterDiscount * (vatPercent / 100);
              const totalLine = afterDiscount + vat;
              return (
                <View key={index} style={[quotationPdfStyles.tableRow, index % 2 === 1 ? quotationPdfStyles.tableRowAlt : {}]}>
                  <Text style={quotationPdfStyles.col1}>{line?.description || 'Item'}</Text>
                  <Text style={quotationPdfStyles.col2}>{quantity}</Text>
                  <Text style={quotationPdfStyles.col3}>{line?.unit || 'each'}</Text>
                  <Text style={quotationPdfStyles.col4}>{formatCurrencyPdf(unitPrice)}</Text>
                  <Text style={quotationPdfStyles.col5}>{formatCurrencyPdf(discount)}</Text>
                  <Text style={quotationPdfStyles.col6}>{vatPercent || 0}%</Text>
                  <Text style={quotationPdfStyles.col7}>{formatCurrencyPdf(totalLine)}</Text>
                </View>
              );
            })}
            {/* <View style={quotationPdfStyles.discountTotalRow}>
              <Text style={[quotationPdfStyles.col1, quotationPdfStyles.col2, quotationPdfStyles.col3, quotationPdfStyles.col4, { fontSize: 11, fontWeight: 'bold' }]}>Total Discount:</Text>
              <Text style={[quotationPdfStyles.col5, { fontSize: 11, fontWeight: 'bold', color: '#dc2626' }]}>{formatCurrencyPdf(totalDiscount)}</Text>
              <Text style={[quotationPdfStyles.col6, quotationPdfStyles.col7]}></Text>
            </View> */}
          </View>

          <View style={quotationPdfStyles.totals}>
            <View style={quotationPdfStyles.totalRow}>
              <Text style={quotationPdfStyles.totalLabel}>Subtotal:</Text>
              <Text style={quotationPdfStyles.totalValue}>{formatCurrencyPdf(subtotal)}</Text>
            </View>
            <View style={quotationPdfStyles.totalRow}>
              <Text style={quotationPdfStyles.totalLabel}>Less: Discount:</Text>
              <Text style={quotationPdfStyles.totalValue}>({formatCurrencyPdf(totalDiscount)})</Text>
            </View>
            <View style={quotationPdfStyles.totalRow}>
              <Text style={quotationPdfStyles.totalLabel}>Taxable Amount:</Text>
              <Text style={quotationPdfStyles.totalValue}>{formatCurrencyPdf(taxableAmount)}</Text>
            </View>
            <View style={quotationPdfStyles.totalRow}>
              <Text style={quotationPdfStyles.totalLabel}>VAT (5%):</Text>
              <Text style={quotationPdfStyles.totalValue}>{formatCurrencyPdf(vatTotal)}</Text>
            </View>
            <View style={[quotationPdfStyles.totalRow, { borderTopWidth: 2, borderTopColor: '#0891b2', paddingTop: 10, marginTop: 5 }]}>
              <Text style={{ flex: 1, textAlign: 'right', marginRight: 10, fontSize: 12, fontWeight: 'bold', color: '#0891b2' }}>Quoted Amount: (AED)</Text>
              <Text style={[quotationPdfStyles.totalValue, { fontWeight: 'bold', fontSize: 12, color: '#0891b2' }]}>{formatCurrencyPdf(total)}</Text>
            </View>
          </View>

          {quotation?.notes && (
            <View style={quotationPdfStyles.notes}>
              <Text style={quotationPdfStyles.notesTitle}>Terms & Conditions:</Text>
              <Text style={quotationPdfStyles.notesText}>{quotation.notes}</Text>
            </View>
          )}

          <View style={quotationPdfStyles.footer}>
            <Text>This is a computer generated quotation</Text>
            {/* <Text style={quotationPdfStyles.compliance}>
              🇦🇪 UAE Tax Compliant | TRN: 104836477000001
            </Text> */}
          </View>
        </Page>
      </Document>
    );
  };

  const handleDownloadPdf = async (quotation) => {
    try {
      // Validate quotation data
      if (!quotation) {
        throw new Error('Quotation data is missing');
      }

      const quotationNo = quotation.quotation_no || quotation.quotationNo || quotation.id || 'Quotation';
      
      // Generate PDF with better error handling
      const pdfDocument = React.createElement(QuotationPDFDocument, { quotation });
      const pdfGenerator = pdf(pdfDocument);
      const blob = await pdfGenerator.toBlob();
      
      if (!blob || blob.size === 0) {
        throw new Error('PDF blob is empty');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quotation-${quotationNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert(`Unable to generate PDF: ${err.message}`);
    }
  };

  const QuotationsTablePDFDocument = () => {
    const totalAmount = filteredQuotations.reduce((sum, quotation) => sum + calculateQuotationTotal(quotation), 0);
    const totalVAT = filteredQuotations.reduce((sum, quotation) => sum + calculateQuotationVAT(quotation), 0);

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={quotationPdfStyles.page}>
          <View style={{ padding: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, color: '#0891b2' }}>
              Quotation Listing Report
            </Text>
            <Text style={{ fontSize: 10, color: '#666', marginBottom: 10 }}>
              Generated on {formatDate(new Date().toISOString())}
            </Text>

            <View style={quotationPdfStyles.table}>
              <View style={quotationPdfStyles.tableHeader}>
                <Text style={{ width: '20%', paddingHorizontal: 5 }}>Quotation No</Text>
                <Text style={{ width: '25%', paddingHorizontal: 5 }}>Customer</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center' }}>Date</Text>
                <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right' }}>Amount</Text>
                <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right' }}>VAT</Text>
              </View>

              {filteredQuotations.map((quotation, index) => (
                <View key={index} style={[quotationPdfStyles.tableRow, index % 2 === 1 ? quotationPdfStyles.tableRowAlt : {}]}>
                  <Text style={{ width: '20%', paddingHorizontal: 5, fontSize: 10 }}>
                    {quotation.quotation_no || quotation.quotationNo}
                  </Text>
                  <Text style={{ width: '25%', paddingHorizontal: 5, fontSize: 10 }}>
                    {quotation.customer?.name || quotation.customerName || 'Unknown'}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center', fontSize: 10 }}>
                    {formatDate(quotation.quotation_date || quotation.date)}
                  </Text>
                  <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10 }}>
                    {formatCurrencyPdf(calculateQuotationTotal(quotation))}
                  </Text>
                  <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10, color: '#b45309' }}>
                    {formatCurrencyPdf(calculateQuotationVAT(quotation))}
                  </Text>
                </View>
              ))}

              <View style={[quotationPdfStyles.tableRow, { backgroundColor: '#f3f4f6', fontWeight: 'bold' }]}>
                <Text style={{ width: '60%', paddingHorizontal: 5, fontSize: 11, fontWeight: 'bold', textAlign: 'right' }}>
                  TOTAL:
                </Text>
                <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold' }}>
                  {formatCurrencyPdf(totalAmount)}
                </Text>
                <Text style={{ width: '20%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold', color: '#b45309' }}>
                  {formatCurrencyPdf(totalVAT)}
                </Text>
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  const handleExportTablePdf = async () => {
    try {
      const pdfDocument = React.createElement(QuotationsTablePDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotations-list-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Quotations</h2>
          <p className="text-sm text-gray-600">Manage all your quotations</p>
        </div>
        <Link to="/quotations/create" className="btn btn-primary btn-sm sm:btn-md w-full sm:w-auto">
          ➕ Create Quotation
        </Link>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Quotation No
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter quotation number..."
              value={searchQuotationNo}
              onChange={(e) => setSearchQuotationNo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Customer
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter customer name..."
              value={searchCustomer}
              onChange={(e) => setSearchCustomer(e.target.value)}
            />
          </div>
        </div>
        {(searchQuotationNo || searchCustomer) && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {filteredQuotations.length} of {quotations.length} quotations
            </span>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => {
                setSearchQuotationNo('');
                setSearchCustomer('');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Loading quotations from the server...
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Quotations Table - Desktop View */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden hidden md:block">
        {filteredQuotations.length > 0 && (
          <div className="p-4 border-b border-gray-200 flex justify-end">
            <button
              onClick={handleExportTablePdf}
              className="btn btn-sm btn-outline btn-primary"
            >
              📥 Export Table to PDF
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead className="bg-gray-50">
              <tr>
                <th>Quotation No</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>VAT</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.map((quotation) => (
                <tr key={quotation.id} className="hover:bg-gray-50">
                  <td className="font-medium text-primary">
                    {quotation.quotation_no || quotation.quotationNo}
                  </td>
                  <td>
                    {quotation.customer?.name || quotation.customerName || 'Unknown'}
                  </td>
                  <td>
                    {formatDate(quotation.quotation_date || quotation.date)}
                  </td>
                  <td className="font-mono">
                    {formatCurrency(calculateQuotationTotal(quotation))}
                  </td>
                  <td className="font-mono text-amber-600 font-medium">
                    {formatCurrency(calculateQuotationVAT(quotation))}
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => setPreviewQuotation(quotation)}
                      >
                        👁️ View
                      </button>
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => handleDownloadPdf(quotation)}
                      >
                        📄 PDF
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost btn-info text-white"
                        onClick={() => navigate(`/quotations/edit/${quotation.id}`)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost btn-error text-white"
                        onClick={() => handleDelete(quotation.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredQuotations.length > 0 && (
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td colSpan="3" className="text-right pr-4">TOTAL:</td>
                  <td className="font-mono text-gray-900">
                    {formatCurrency(
                      filteredQuotations.reduce((sum, quotation) => sum + calculateQuotationTotal(quotation), 0)
                    )}
                  </td>
                  <td className="font-mono text-amber-600">
                    {formatCurrency(
                      filteredQuotations.reduce((sum, quotation) => sum + calculateQuotationVAT(quotation), 0)
                    )}
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredQuotations.length === 0 && quotations.length > 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching quotations</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setSearchQuotationNo('');
                setSearchCustomer('');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {quotations.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quotations yet</h3>
            <p className="text-gray-600 mb-4">Create your first quotation to get started</p>
            <Link to="/quotations/create" className="btn btn-primary">
              Create Your First Quotation
            </Link>
          </div>
        )}
      </div>

      {/* Quotations Cards - Mobile View */}
      <div className="md:hidden space-y-3">
        {filteredQuotations.length === 0 && quotations.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching quotations</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setSearchQuotationNo('');
                setSearchCustomer('');
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : filteredQuotations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quotations yet</h3>
            <p className="text-gray-600 mb-4">Create your first quotation to get started</p>
            <Link to="/quotations/create" className="btn btn-primary btn-sm">
              Create Your First Quotation
            </Link>
          </div>
        ) : (
          filteredQuotations.map((quotation) => (
            <div key={quotation.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div>
                    <p className="font-medium text-primary text-sm">
                      {quotation.quotation_no || quotation.quotationNo}
                    </p>
                    <p className="text-gray-600 text-xs">
                      {quotation.customer?.name || quotation.customerName || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(quotation.quotation_date || quotation.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium font-mono">{formatCurrency(calculateQuotationTotal(quotation))}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  className="btn btn-xs btn-ghost flex-1"
                  onClick={() => setPreviewQuotation(quotation)}
                >
                  👁️ View
                </button>
                <button
                  className="btn btn-xs btn-ghost flex-1"
                  onClick={() => handleDownloadPdf(quotation)}
                >
                  📄 PDF
                </button>
                <button 
                  className="btn btn-xs btn-ghost btn-info text-white flex-1"
                  onClick={() => navigate(`/quotations/edit/${quotation.id}`)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn btn-xs btn-ghost btn-error text-white flex-1"
                  onClick={() => handleDelete(quotation.id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {previewQuotation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/10">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold">Quotation Preview</h3>
                <p className="text-sm text-gray-500">
                  {previewQuotation.quotation_no || previewQuotation.quotationNo}
                </p>
              </div>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setPreviewQuotation(null)}
              >
                Close
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Quotation</p>
                  <p className="font-semibold text-gray-900">
                    {previewQuotation.quotation_no || previewQuotation.quotationNo}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Date</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(previewQuotation.quotation_date || previewQuotation.date)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Customer</p>
                <p className="font-semibold text-gray-900">
                  {previewQuotation.customer?.name || previewQuotation.customerName || 'Unknown'}
                </p>
                <p className="text-sm text-gray-600">
                  {previewQuotation.customer?.phone || previewQuotation.customerPhone || ''}
                </p>
                <p className="text-sm text-gray-600">
                  {previewQuotation.customer?.address || previewQuotation.customerAddress || ''}
                </p>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">VAT %</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(previewQuotation.qlines || previewQuotation.items || []).map((line, lineIndex) => {
                      const quantity = parseFloat(line.quantity || 0);
                      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
                      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
                      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
                      const subtotal = quantity * unitPrice;
                      const afterDiscount = subtotal - discount;
                      const vat = afterDiscount * (vatPercent / 100);
                      const total = afterDiscount + vat;

                      return (
                        <tr key={lineIndex}>
                          <td className="px-4 py-3 text-sm text-gray-700">{line.description}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{quantity}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(unitPrice)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(discount)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{vatPercent}%</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-4 justify-end md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {formatCurrency(calculateQuotationTotal(previewQuotation))}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Notes</p>
                  <p className="mt-2 text-sm text-gray-700">{previewQuotation.notes || 'No notes provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationList;